import { useCallback, useId, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ListItem from 'LuxKit/List/ListItem'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useOrderFlightDetails from 'hooks/Flights/useOrderFlightDetails'
import { useTripDateFormatters } from 'tripPlanner/components/Chat/useTripDateFormatters'
import type { OrderTileData } from './bookingSelectionUtils'
import { deriveFlightDisplayData, formatFlightFallbackTitle } from 'tripPlanner/components/Chat/flightDisplayUtils'
import { getOrderTypeIcon } from './orderTypeIcon'
import { formatUpcomingTripSummary, getUpcomingTripTitle } from './upcomingTrips'

const PLAN_ICON = <LineAngleRightIcon />
const PLAN_BUSY_ICON = <LoadingAnimation size={8} />

// Below this the details get too cramped, so the CTA wraps onto its own line.
const DETAILS_MIN_INLINE_SIZE = rem(280)

// The card's dividers run edge to edge, so each row owns its own inset.
const RowLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(10)} ${rem(14)};
  padding: ${rem(12)} ${rem(14)};
`

// The row owns the spacing (the design's), so the list item's own padding and gap give way to it.
const TripListItem = styled(ListItem)`
  && {
    gap: ${rem(14)};
    padding: 0;
  }
`

const Details = styled.div`
  flex: 1 1 ${DETAILS_MIN_INLINE_SIZE};
`

const PlanButtonWrapper = styled(CSSBreakpoint)`
  white-space: nowrap;
`

interface Props {
  trip: OrderTileData
  disabled: boolean
  isBusy: boolean
  onPlanClick: (trip: OrderTileData) => void
}

function ChatLandingUpcomingTripRow({ trip, disabled, isBusy, onPlanClick }: Props) {
  const intl = useIntl()
  const formatters = useTripDateFormatters()

  const [{ flightDetails }] = useOrderFlightDetails(trip.orderId, [trip.primaryFlightItemId])
  const flight = useMemo(() => flightDetails && deriveFlightDisplayData(flightDetails), [flightDetails])

  const tripName = getUpcomingTripTitle(intl, trip, flight)
  const summary = formatUpcomingTripSummary(intl, trip, formatters, flight)
  const detailsId = useId()

  const handlePlanClick = useCallback(() => {
    onPlanClick(trip)
  }, [onPlanClick, trip])

  const thumbnail = useMemo(() => (
    <ListItemThumbnail gutter={trip.imageId ? 'none' : 'S'} kind="neutral">
      {trip.imageId && (
        <ResponsiveImage
          id={trip.imageId}
          alt=""
          width={48}
          height={48}
          sizes={rem(48)}
          aspectRatio="1:1"
          objectFit="cover"
          loading="lazy"
        />
      )}
      {!trip.imageId && getOrderTypeIcon(trip.primaryType)}
    </ListItemThumbnail>
  ), [trip.imageId, trip.primaryType])
  const planIcon = isBusy ? PLAN_BUSY_ICON : PLAN_ICON
  const planLabel = intl.formatMessage({
    id: '2tbUH_ofyzV8RtdaZ-uH',
    defaultMessage: 'Plan around this trip',
    description: 'Button on an upcoming booking in the trip planner chat landing page that starts a chat planning around that booking',
  })

  return (
    <RowLayout>
      <Details id={detailsId}>
        <TripListItem size="medium" title={tripName ?? formatFlightFallbackTitle(intl)} subtitle={summary} startIcon={thumbnail} />
      </Details>
      <PlanButtonWrapper max="mobile">
        <TextButton
          kind="secondary"
          size="small"
          endIcon={planIcon}
          disabled={disabled}
          onClick={handlePlanClick}
          aria-describedby={detailsId}
          aria-busy={isBusy}
        >
          {planLabel}
        </TextButton>
      </PlanButtonWrapper>
      <PlanButtonWrapper min="tablet">
        <TextButton
          kind="secondary"
          size="medium"
          endIcon={planIcon}
          disabled={disabled}
          onClick={handlePlanClick}
          aria-describedby={detailsId}
          aria-busy={isBusy}
        >
          {planLabel}
        </TextButton>
      </PlanButtonWrapper>
    </RowLayout>
  )
}

export default ChatLandingUpcomingTripRow
